import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import { useQueryState } from 'nuqs';
import React, { useCallback, useRef, useState } from 'react';
import { Redirect, useHistory, useLocation } from 'react-router-dom';
import { isLevitateHostedSite } from '../../../extViewmodels/sdk/models';
import { useUserSession } from '../../../models/hooks/appStateHooks';
import {
	invalidateClientSiteQueries,
	useClientSitemapQuery,
	useClientSitePendingChangesQuery,
	useClientSiteQuery,
} from '../../../queries/Website/useClientSiteQuery';
import { AppBarNavigationItemPortalDestinationId } from '../../components-v2/AppLayout/components/AppBar';
import {
	AppDrawer,
	AppDrawerToolbar,
	AppDrawerToolbarButton,
	StickyAppDrawerContent,
} from '../../components-v2/Drawer/AppDrawer';
import { LoadingSpinner } from '../../components/LoadingSpinner';
import { SparklesIcon } from '../../components/svgs/icons/SparklesIcon';
import { TabContent, TabList, TabRoot, TabTrigger } from '../../components-v2/Tabs';
import { Portal } from '../../components/Portal';
import { styleSheet as s } from './styles';
import { WebsiteMetrics } from './WebsiteMetrics/WebsiteMetrics';
import { WebsiteAssistantChat, WebsiteAssistantChatHandle } from './WebsiteAssistantChat';
import { WebsiteBreadcrumbs } from './WebsiteBreadcrumbs';
import { PreviewViewport, WebsitePreview } from './WebsitePreview';
import { WEBSITE_EDITOR_PATH, WEBSITE_PATH } from './routes';

/**
 * The site's traffic (Metrics, the default) and, for people who can change it, the editor: a read-only preview with
 * Lev beside it, where every change is described in chat and applied by the agent.
 */
const WEBSITE_ASSISTANT_DRAWER_VALUE = 'website-assistant';

export function WebsitePage() {
	const userSession = useUserSession();
	const hosted = isLevitateHostedSite(userSession.account.features?.websites);

	const siteQuery = useClientSiteQuery({ enabled: hosted });
	const sitemapQuery = useClientSitemapQuery({ enabled: hosted && Boolean(siteQuery.data) });
	const pendingQuery = useClientSitePendingChangesQuery({ enabled: hosted && Boolean(siteQuery.data) });

	// The page in view lives in the route (#/website?page=/about) so the browser's back and forward buttons step
	// through pages, a refresh lands where you were, and a page can be deep-linked. User navigation pushes;
	// agent-driven jumps replace, so stepping back doesn't retrace the assistant's moves.
	const history = useHistory();
	const location = useLocation();
	const [pageParam, setPageParam] = useQueryState('page');
	const pagePath = normalizePagePath(pageParam);
	const setPagePath = (path: string, replace = false) => {
		const normalized = normalizePagePath(path);
		if (normalized === pagePath) {
			return;
		}
		setPageParam(normalized === '/' ? null : normalized, { history: replace ? 'replace' : 'push' });
	};
	const chatRef = useRef<WebsiteAssistantChatHandle>(null);
	// Lev is the point of this screen, so the drawer opens with it rather than restoring whatever drawer was last
	// open elsewhere in the app.
	const [activeDrawer, setActiveDrawer] = useState(WEBSITE_ASSISTANT_DRAWER_VALUE);
	const [reloadKey, setReloadKey] = useState(0);
	const [viewport, setViewport] = useState<PreviewViewport>('desktop');

	// No homepage fallback: an unresolved path used to mean a vague hint to the agent, but it now addresses an
	// element index, and pairing that with the wrong page's id edits the wrong page.
	const currentPageId = sitemapQuery.data?.find(n => n.resolvedUrl === pagePath)?.pageId;

	// From a change card: navigate to the page that changed and reload the frame, and refresh the pending banner.
	// Announced-on-mount cards (userInitiated false) fire once per card, including on history load — the reload
	// is harmless there and means a returning user always sees the current draft.
	const handleWebsiteChange = useCallback(
		(change: Api.WebsiteChangeToolCall) => {
			if (change.pagePath) {
				setPagePath(change.pagePath, true);
			}
			setReloadKey(k => k + 1);
			invalidateClientSiteQueries();
		},
		[setPagePath]
	);

	if (!hosted) {
		return <Redirect to='/dashboard' />;
	}

	if (siteQuery.isLoading) {
		return (
			<div className={css(s.empty)}>
				<LoadingSpinner />
			</div>
		);
	}

	if (siteQuery.isError) {
		return (
			<div className={css(s.empty)} role='alert'>
				<div className={css(s.emptyTitle)}>We couldn’t load your website</div>
				<div>Refresh to try again, or contact us if it keeps happening.</div>
			</div>
		);
	}

	if (!siteQuery.data) {
		return (
			<div className={css(s.empty)}>
				<div className={css(s.emptyTitle)}>Your website is being built</div>
				<div>It will show up here as soon as the Levitate team has set it up.</div>
			</div>
		);
	}

	// Picking an element aims a content change, which managePages alone doesn't grant — it covers page rename, SEO
	// and deletion, and reaches no element. The lock is already subtracted from every verb by the API.
	const { access } = siteQuery.data;
	const canEdit = access.editContent || access.editElements || access.managePages;
	const canSelectElements = Boolean(currentPageId) && (access.editContent || access.editElements);
	const canEditImages = Boolean(currentPageId) && access.editContent && !access.locked;
	// Someone who can't change the site has nothing to do in the editor, so they get Metrics alone, not a dead tab.
	const view = canEdit && location.pathname.startsWith(WEBSITE_EDITOR_PATH) ? 'editor' : 'metrics';
	// Both tabs are one route, so switching only changes the path: the editor (and a Lev reply still streaming into
	// it) stays mounted. The query string carries over so the open page and the chosen range survive the round trip.
	const showView = (next: string) =>
		history.push({ pathname: next === 'editor' ? WEBSITE_EDITOR_PATH : WEBSITE_PATH, search: location.search });
	const openEditor = canEdit ? () => showView('editor') : undefined;

	return (
		<TabRoot className={css(s.page)} value={view} onValueChange={showView}>
			<Portal destination={AppBarNavigationItemPortalDestinationId}>
				<WebsiteBreadcrumbs sitemap={sitemapQuery.data} pagePath={view === 'editor' ? pagePath : undefined} />
			</Portal>
			{canEdit ? (
				<TabList styles={[s.tabList]}>
					<TabTrigger value='metrics'>Metrics</TabTrigger>
					<TabTrigger value='editor'>Editor</TabTrigger>
				</TabList>
			) : null}
			<TabContent value='metrics' className={css(s.tabPanel)}>
				<WebsiteMetrics onOpenEditor={openEditor} />
			</TabContent>
			{canEdit ? (
				// Kept mounted while Metrics shows: a Lev turn streams into the chat, and unmounting mid-turn drops its
				// result and lets a second turn start before the first finishes.
				<TabContent value='editor' forceMount className={css(s.tabPanel)}>
					<WebsitePreview
						site={siteQuery.data}
						sitemap={sitemapQuery.data}
						pendingChanges={pendingQuery.data}
						pagePath={pagePath}
						onPagePathChange={path => setPagePath(path)}
						reloadKey={reloadKey}
						viewport={viewport}
						onViewportChange={setViewport}
						canSelectElements={canSelectElements}
						pageId={currentPageId}
						canEditImages={canEditImages}
						onDraftChanged={() => {
							setReloadKey(k => k + 1);
							invalidateClientSiteQueries();
						}}
						onElementSubmit={(selection, text) => Boolean(chatRef.current?.submitElementEdit(selection, text))}
					/>
					<AppDrawer
						value={activeDrawer}
						onValueChange={setActiveDrawer}
						styles={view === 'editor' ? undefined : [s.hidden]}
					>
						<StickyAppDrawerContent value={WEBSITE_ASSISTANT_DRAWER_VALUE}>
							<WebsiteAssistantChat
								ref={chatRef}
								currentPageId={currentPageId}
								canEdit={canEdit}
								onWebsiteChange={handleWebsiteChange}
							/>
						</StickyAppDrawerContent>
						<AppDrawerToolbar>
							<AppDrawerToolbarButton value={WEBSITE_ASSISTANT_DRAWER_VALUE} tooltip='Edit with Lev'>
								<SparklesIcon />
							</AppDrawerToolbarButton>
						</AppDrawerToolbar>
					</AppDrawer>
				</TabContent>
			) : null}
		</TabRoot>
	);
}

/** `/`, `/about`, `/services/roofing` — no trailing slash, always a leading one, never a query or fragment. */
function normalizePagePath(value: string | null | undefined): string {
	const trimmed = (value ?? '').split(/[?#]/)[0].trim().replace(/\/+$/, '');
	if (!trimmed) {
		return '/';
	}
	return trimmed.startsWith('/') ? trimmed : `/${trimmed}`;
}
